import React, { useState, useEffect } from "react";
import { Edit2, Clock, CreditCard, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { ReceiptsList } from "@/components/receipts/ReceiptsList";

export const PaymentHistory = ({ studentId, refreshKey = 0 }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordingPayment, setRecordingPayment] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_method: "cash",
    cheque_bank_name: "",
    cheque_number: "",
    record_amount: "",
  });
  const [editForm, setEditForm] = useState({
    amount: "",
    payment_method: "cash",
    cheque_bank_name: "",
    cheque_number: "",
    notes: "",
  });

  useEffect(() => {
    if (studentId) {
      fetchPayments();
    }
    // refreshKey bumps from the parent dialog whenever a new enrollment is
    // created, which auto-creates a Payment row. Without this, the cached
    // payments list from when the dialog opened would not include the
    // brand-new row, and the user would see only "old" payments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, refreshKey]);

  const fetchPayments = async () => {
    try {
      const res = await api.get(`/payments?student_id=${studentId}`);
      setPayments(res.data.payments);
    } catch (error) {
      console.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  const recordPayment = async (paymentId) => {
    const payment = payments.find(p => p.payment_id === paymentId);
    const balance = payment?.balance_amount || 0;
    const recordAmount = parseFloat(paymentForm.record_amount) || balance;

    if (recordAmount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (recordAmount > balance) {
      toast.error(`Amount cannot exceed outstanding balance of ₹${balance}`);
      return;
    }

    try {
      await api.post(`/payments/${paymentId}/record-partial`, {
        amount: recordAmount,
        payment_method: paymentForm.payment_method,
        cheque_bank_name: paymentForm.payment_method === "cheque" ? paymentForm.cheque_bank_name : null,
        cheque_number: paymentForm.payment_method === "cheque" ? paymentForm.cheque_number : null,
      });
      toast.success(`₹${recordAmount} recorded successfully`);
      setRecordingPayment(null);
      setPaymentForm({ payment_method: "cash", cheque_bank_name: "", cheque_number: "", record_amount: "" });
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const startEditPayment = (payment) => {
    setEditingPayment(payment.payment_id);
    setEditForm({
      amount: payment.amount.toString(),
      payment_method: payment.payment_method || "cash",
      cheque_bank_name: payment.cheque_bank_name || "",
      cheque_number: payment.cheque_number || "",
      notes: payment.notes || "",
    });
  };

  const saveEditPayment = async (paymentId) => {
    try {
      await api.put(`/payments/${paymentId}`, {
        amount: parseFloat(editForm.amount),
        payment_method: editForm.payment_method,
        cheque_bank_name: editForm.payment_method === "cheque" ? editForm.cheque_bank_name : null,
        cheque_number: editForm.payment_method === "cheque" ? editForm.cheque_number : null,
        notes: editForm.notes,
      });
      toast.success("Payment updated successfully");
      setEditingPayment(null);
      setEditForm({ amount: "", payment_method: "cash", cheque_bank_name: "", cheque_number: "", notes: "" });
      fetchPayments();
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (payments.length === 0) {
    return <p className="text-center text-slate-600 py-8">No payment history</p>;
  }

  return (
    <div className="space-y-3">
      {payments.map((payment) => {
        const totalAmount = payment.total_amount || payment.amount || 0;
        const amountPaid = payment.amount_paid || (payment.payment_status === "completed" ? totalAmount : 0);
        const balanceAmount = payment.balance_amount || (totalAmount - amountPaid);
        const isPartial = payment.payment_status === "partial" || (balanceAmount > 0 && amountPaid > 0);
        const isPending = payment.payment_status === "pending" || (amountPaid === 0 && balanceAmount > 0);

        return (
        <div
          key={payment.payment_id}
          className={`p-4 rounded-xl border ${
            isPending
              ? "bg-orange-50 border-orange-200"
              : isPartial
                ? "bg-amber-50 border-amber-200"
                : "bg-slate-50 border-slate-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-slate-900">₹{totalAmount}</p>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  isPending
                    ? "bg-orange-100 text-orange-700"
                    : isPartial
                      ? "bg-amber-100 text-amber-700"
                      : "bg-green-100 text-green-700"
                }`}>
                  {isPending ? "Pending" : isPartial ? "Partial" : "Paid"}
                </span>
                {isPartial && (
                  <span className="text-xs text-amber-600">
                    (Paid: ₹{amountPaid} | Balance: ₹{balanceAmount})
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 mt-1">
                {payment.batch?.name || "N/A"}
              </p>
              {!isPending && (
                <>
                  <p className="text-xs text-slate-500 mt-1">
                    {payment.payment_method?.toUpperCase()} • {payment.payment_date && format(new Date(payment.payment_date), "dd MMM yyyy")}
                  </p>
                  {payment.payment_method === "cheque" && payment.cheque_bank_name && (
                    <p className="text-xs text-slate-500">
                      {payment.cheque_bank_name} - {payment.cheque_number}
                    </p>
                  )}
                </>
              )}
              {payment.is_edited && payment.last_edited_at && (
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Edited on {format(new Date(payment.last_edited_at), "dd MMM yyyy, hh:mm a")}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              {(isPending || isPartial) ? (
                <Button
                  size="sm"
                  onClick={() => setRecordingPayment(payment.payment_id)}
                  className="rounded-full bg-green-500 hover:bg-green-600 text-white"
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  {isPartial ? "Pay Balance" : "Record Payment"}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => startEditPayment(payment)}
                  variant="outline"
                  className="rounded-full"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
          </div>

          {/* Receipts (auto-generated on each recorded transaction) */}
          {amountPaid > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-200/70">
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Receipts</p>
              <ReceiptsList paymentId={payment.payment_id} refreshKey={payment.amount_paid || 0} />
            </div>
          )}

          {recordingPayment === payment.payment_id && (
            <div className="mt-4 pt-4 border-t border-orange-200 space-y-3">
              <div className="space-y-2">
                <Label className="text-sm">Amount to Pay (₹)</Label>
                <Input
                  data-testid="record-payment-amount"
                  type="number"
                  value={paymentForm.record_amount}
                  onChange={(e) => setPaymentForm({...paymentForm, record_amount: e.target.value})}
                  placeholder={`Max: ₹${balanceAmount}`}
                  max={balanceAmount}
                  className="h-10 rounded-lg"
                />
                <p className="text-xs text-slate-500">Outstanding: ₹{balanceAmount}. Pay any amount.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Payment Method</Label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                  className="w-full h-10 rounded-lg border px-3 bg-white"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>

              {paymentForm.payment_method === "cheque" && (
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={paymentForm.cheque_bank_name}
                    onChange={(e) => setPaymentForm({...paymentForm, cheque_bank_name: e.target.value})}
                    placeholder="Bank name"
                    className="h-10 rounded-lg"
                  />
                  <Input
                    value={paymentForm.cheque_number}
                    onChange={(e) => setPaymentForm({...paymentForm, cheque_number: e.target.value})}
                    placeholder="Cheque number"
                    className="h-10 rounded-lg"
                  />
                </div>
              )}

              {payment.payment_history && payment.payment_history.length > 0 && (
                <div className="space-y-1 p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs font-medium text-slate-600">Payment History:</p>
                  {payment.payment_history.map((txn, idx) => (
                    <div key={`${txn.date || ""}-${txn.amount}-${txn.method || ""}-${idx}`} className="flex items-center justify-between text-xs text-slate-600">
                      <span>₹{txn.amount} via {txn.method}</span>
                      <span>{format(new Date(txn.date), "dd MMM yyyy")}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRecordingPayment(null)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-record-payment"
                  size="sm"
                  onClick={() => recordPayment(payment.payment_id)}
                  className="flex-1 rounded-full bg-green-500 hover:bg-green-600"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Record ₹{paymentForm.record_amount || balanceAmount}
                </Button>
              </div>
            </div>
          )}

          {editingPayment === payment.payment_id && (
            <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
              <h4 className="font-medium text-slate-900 text-sm">Edit Payment</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Amount (₹)</Label>
                  <Input
                    type="number"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                    className="h-9 rounded-lg text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Payment Method</Label>
                  <select
                    value={editForm.payment_method}
                    onChange={(e) => setEditForm({...editForm, payment_method: e.target.value})}
                    className="w-full h-9 rounded-lg border px-3 bg-white text-sm"
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>

              {editForm.payment_method === "cheque" && (
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={editForm.cheque_bank_name}
                    onChange={(e) => setEditForm({...editForm, cheque_bank_name: e.target.value})}
                    placeholder="Bank name"
                    className="h-9 rounded-lg text-sm"
                  />
                  <Input
                    value={editForm.cheque_number}
                    onChange={(e) => setEditForm({...editForm, cheque_number: e.target.value})}
                    placeholder="Cheque number"
                    className="h-9 rounded-lg text-sm"
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingPayment(null)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => saveEditPayment(payment.payment_id)}
                  className="flex-1 rounded-full bg-primary"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Save
                </Button>
              </div>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
};

export default PaymentHistory;
